import {
  AnswerType,
  BaseQuestionResource,
  QuestionType,
} from "../types/BaseQuestionResource";
import {
  DisclosureStatus,
  EdhConditionDisclosureResource,
} from "../types/EdhConditionDisclosureResource";

/**
 * Returns true if base question is answered.
 * Only checks the base question itself, disclosures must be checked using
 * ex. the "questionHasRemainingDisclosures" function.
 * @param baseQuestion A base question object.
 */
export const isBaseQuestionAnswered = (
  baseQuestion: BaseQuestionResource,
): boolean => {
  const {
    answerRequiresAction,
    answerText,
    answerType,
    answerValue,
    disclosures,
    disclosureSource,
    questionType,
    visible,
  } = baseQuestion;

  // Question is not visible, move on.
  if (!visible) {
    return true;
  }

  // Headings are not interactive.
  if (questionType === QuestionType.HEADING) {
    return true;
  }

  switch (answerType) {
    case AnswerType.PICKLIST:
    case AnswerType.ENTER_DETAILS: {
      if (answerValue && answerText && answerText?.length > 0) {
        return true;
      }
      return false;
    }
    case AnswerType.YES_NO: {
      // Handle YES_NO with attached PICKLIST disclosures
      if (
        answerRequiresAction &&
        answerValue === "true" &&
        disclosureSource === "PICKLIST" &&
        (!disclosures || disclosures.length === 0)
      ) {
        return false;
      } else if (answerValue && answerText && answerText?.length > 0) {
        return true;
      }
      return false;
    }
    // Perform generic check if answerType is missing.
    default:
      if (answerValue && !answerRequiresAction) {
        return true;
      }
      return false;
  }
};

/**
 * Returns true if any open disclosures are not CONFIRMED.
 * @param baseQuestion A base question object.
 * @param embeddedDisclosures The list of embedded disclosures.
 */
export const questionHasRemainingDisclosures = (
  baseQuestion: BaseQuestionResource,
  embeddedDisclosures: EdhConditionDisclosureResource[] = [],
) => {
  const { disclosures } = baseQuestion;
  if (disclosures && disclosures.length > 0) {
    return (
      disclosures.filter((disclosure: EdhConditionDisclosureResource) => {
        if (
          embeddedDisclosures.filter(
            (embeddedDisclosure: EdhConditionDisclosureResource) => {
              if (
                baseQuestion.answerRequiresAction &&
                embeddedDisclosure.aliasName === disclosure.aliasName &&
                embeddedDisclosure.status !== DisclosureStatus.CONFIRMED &&
                embeddedDisclosure.status !== DisclosureStatus.CANCELLED
              ) {
                return embeddedDisclosure;
              }
              return null;
            },
          ).length > 0
        ) {
          return disclosure;
        }
        return null;
      }).length > 0
    );
  }
  return false;
};
